import { useTranslation } from 'react-i18next';
import { CheckCircle2, AlertTriangle, XCircle, MinusCircle } from 'lucide-react';

export type ComplianceStatus = 'valid' | 'expiring_soon' | 'expired' | 'unknown';

/**
 * Computes overall compliance status from a vehicle's document expiry dates.
 *
 *   expired       → any document already past expiry
 *   expiring_soon → any document expiring within 30 days
 *   valid         → all docs valid > 30 days
 *   unknown       → no documents recorded yet
 */
export function computeComplianceStatus(
  vehicle: {
    istimaraExpiry: string | null;
    insuranceExpiry: string | null;
    naqliatCardExpiryDate: string | null;
    inspectionNextDueDate: string | null;
  }
): ComplianceStatus {
  const dates = [
    vehicle.istimaraExpiry,
    vehicle.insuranceExpiry,
    vehicle.naqliatCardExpiryDate,
    vehicle.inspectionNextDueDate,
  ].filter((d): d is string => d != null);

  if (dates.length === 0) return 'unknown';

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const thirtyDays = new Date(today.getTime() + 30 * 24 * 60 * 60 * 1000);

  let hasExpired = false;
  let hasExpiringSoon = false;
  for (const d of dates) {
    const expiry = new Date(d);
    if (expiry < today) hasExpired = true;
    else if (expiry <= thirtyDays) hasExpiringSoon = true;
  }
  if (hasExpired) return 'expired';
  if (hasExpiringSoon) return 'expiring_soon';
  return 'valid';
}

export function ComplianceBadge({ status, compact = false }: { status: ComplianceStatus; compact?: boolean }) {
  const { t } = useTranslation();

  const styles = {
    valid: { bg: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/30', Icon: CheckCircle2 },
    expiring_soon: { bg: 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/30', Icon: AlertTriangle },
    expired: { bg: 'bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/30', Icon: XCircle },
    unknown: { bg: 'bg-muted text-muted-foreground border-border', Icon: MinusCircle },
  } as const;

  const s = styles[status];
  const Icon = s.Icon;

  if (compact) {
    return (
      <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium border ${s.bg}`}>
        <Icon className="h-3 w-3" />
      </span>
    );
  }

  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium border ${s.bg}`}>
      <Icon className="h-3 w-3" />
      {t(`compliance.${status}`)}
    </span>
  );
}

/**
 * Field-level expiry indicator. Shows colored dot + days-left next to a date input.
 */
export function ExpiryIndicator({ dateString }: { dateString: string | null }) {
  const { t } = useTranslation();
  if (!dateString) return null;

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const expiry = new Date(dateString);
  const daysLeft = Math.ceil((expiry.getTime() - today.getTime()) / (24 * 60 * 60 * 1000));

  let color = 'text-emerald-600 dark:text-emerald-400';
  let label = t('compliance.daysLeft', { days: daysLeft });
  if (daysLeft < 0) {
    color = 'text-red-600 dark:text-red-400';
    label = t('compliance.expiredAgo', { days: Math.abs(daysLeft) });
  } else if (daysLeft <= 30) {
    color = 'text-amber-600 dark:text-amber-400';
  }

  return (
    <span className={`text-[11px] font-medium ${color}`} aria-live="polite">
      {label}
    </span>
  );
}
